import { Box, Flex, Grid } from "@chakra-ui/react";
import moment from "moment";
import WidgetBox from "components/elements/WidgetBox/widget-box";
import PageHeader from "components/layout/PageHeader";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useCallback, useContext, useEffect, useState } from "react";
import StatDiscription from "../elements/StatBox/StatBoxDescription";
import LoanTypeBreakdown from "components/analytics/LoanTypeBreakdown";
import { AnalyticsLoansTable } from "../elements/AnalyticsLoansTable";
import { apiRequest } from "utils/api";
import { SENIOR_MANAGEMENT_FIXED_FIELDS } from "utils/constants";
import { numberToDollar } from "utils/helpers";
import { getCustomCurrency } from "utils/uiconfighelper";
import { AlertsContext } from "providers/alerts";
import LoanPipeline from "components/analytics/LoanPipelineProgress";
import AssertTypeBreakDown from "components/analytics/AssetTypeBreakDown";
import {
  LoanStatusPieType,
  LoanStatusPie as PhoenixLoanStatusPie,
} from "components/analytics/@Phoenix/LoanStatusPie/loan-status-pie";
import { LoanModel } from "providers/loans/loan.model";
import { useCustomizations } from "hooks/customizations/useCustomizations";
import { useLoansQuery } from "modules/loan/queries/use-loans-query";

export function formatPercentageChange(percentageChange: number): string {
  const formattedPercentageChange =
    percentageChange > 0 ? `+${percentageChange}%` : `${percentageChange}%`;

  return formattedPercentageChange;
}

export function determineIndicator(percentageChange: number): string {
  if (percentageChange > 0) {
    return "green";
  } else if (percentageChange < 0) {
    return "red";
  } else {
    return "null";
  }
}

function SeniorManagementAnalytics() {
  const { user } = useTenant();
  const { data: loans = [] } = useLoansQuery();
  const [filteredLoans, setFilteredLoans] = useState<LoanModel[]>([]);
  const { isPhoenix } = useCustomizations();

  const [totalOriginationValues, setTotalOriginationValues] = useState({
    total_origination: 0,
    percentage_change: 0,
  });
  const [totalProcessingValues, setTotalProcessingValues] = useState({
    total_processing: 0,
    percentage_change: 0,
  });
  const [totalPipelineValues, setTotalPipelineValues] = useState({
    total_pipeline: 0,
    percentage_change: 0,
  });
  const [count, setCount] = useState(0);
  const { showError } = useContext(AlertsContext);

  const loadData = useCallback(async () => {
    try {
      const {
        totalOrigination,
        percentageOriginationChange,
        totalProcessing,
        percentageNewLoans,
        totalPipelineAmount,
        percentagePipelineChange,
      } = await apiRequest("/supabase/senior_analytics/select");

      setTotalOriginationValues({
        total_origination: totalOrigination,
        percentage_change: percentageOriginationChange,
      });
      setTotalProcessingValues({
        total_processing: totalProcessing,
        percentage_change: percentageNewLoans,
      });
      setTotalPipelineValues({
        total_pipeline: totalPipelineAmount,
        percentage_change: percentagePipelineChange,
      });
    } catch (error) {
      showError({
        title: "Error",
        description: "Error loading analytics data",
      });
    }
  }, [showError]);

  useEffect(() => {
    loadData();
    const recentLoans = loans.filter(loan => {
      const loanDate = new Date(loan.created_at);
      const thirtyDaysAgo = moment().subtract(30, "days").toDate();
      return loanDate >= thirtyDaysAgo && loanDate <= new Date();
    });
    setCount(recentLoans.length);

    const filtered = loans.filter(
      loan => loan.status.isClosed === true || loan.status.isFunded === true
    );
    setFilteredLoans(filtered || []);
  }, [loadData, loans]);

  return (
    <>
      <PageHeader title="Analytics" />

      <Flex flexDirection="column" gap={4}>
        <Grid templateColumns="repeat(3, 1fr)" gap={4}>
          <StatDiscription
            stat={numberToDollar(totalOriginationValues.total_origination, getCustomCurrency(user))}
            description="Total Originations"
            helpNumber={formatPercentageChange(totalOriginationValues.percentage_change)}
            helpText="than last month"
            helpColor={determineIndicator(totalOriginationValues.percentage_change)}
            number={99}
            mt="-10px"
          />
          <StatDiscription
            stat={count}
            description="New Loans"
            helpNumber={formatPercentageChange(totalProcessingValues.percentage_change)}
            helpText="than last month"
            helpColor={determineIndicator(totalProcessingValues.percentage_change)}
            number={99}
            mt="-10px"
          />
          <StatDiscription
            stat={numberToDollar(totalPipelineValues.total_pipeline, getCustomCurrency(user))}
            description="Pipeline"
            helpNumber={formatPercentageChange(totalPipelineValues.percentage_change)}
            helpText="than last month"
            helpColor={determineIndicator(totalPipelineValues.percentage_change)}
            number={99}
            mt="-10px"
          />
        </Grid>

        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          {isPhoenix ? (
            <>
              <Box flex={1}>
                <PhoenixLoanStatusPie type={LoanStatusPieType.UNCOMPLETED} />
              </Box>
              <Box flex={1}>
                <PhoenixLoanStatusPie type={LoanStatusPieType.COMPLETED} />
              </Box>
            </>
          ) : (
            <>
              <Box flex={1}>
                <LoanTypeBreakdown />
              </Box>
              <Box flex={1}>
                <AssertTypeBreakDown />
              </Box>
              {/* <LoanStatusLegends /> */} {/* Do not remove this comment*/}
            </>
          )}
        </Grid>

        <WidgetBox title="Loan Pipeline">
          <LoanPipeline />
        </WidgetBox>

        <WidgetBox>
          <AnalyticsLoansTable columnsData={SENIOR_MANAGEMENT_FIXED_FIELDS} loans={filteredLoans} />
        </WidgetBox>
      </Flex>
    </>
  );
}

export default SeniorManagementAnalytics;
